import { FC } from 'react';
import { useSetRecoilState } from 'recoil';
import { ApolloError, useMutation } from '@apollo/client';
import * as yup from 'yup';
import { Formik } from 'formik';

import ResizableTextArea from 'components/common/Input/ResizableTextArea';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { ADD_FILTER, GET_BILLING_AND_USAGE_DATA, UPDATE_FILTER } from 'components/billingAndUsage/queries';
import { DiscardFilterType } from 'components/billingAndUsage/types';
import ELEMENT from 'common/element';

type FormValues = {
  filterValue: string;
};

type FormProps = {
  filter: DiscardFilterType;
  onClose: () => void;
};

export const FilterValueForm: FC<FormProps> = ({ filter, onClose }: FormProps) => {
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  const handleError = (message: string): void => {
    setPageAlertConfig({
      message,
      variant: PageAlertVariant.ERROR,
      autoHide: true,
      duration: 3,
    });
  };

  const [createFilter] = useMutation(ADD_FILTER, {
    refetchQueries: [
      {
        query: GET_BILLING_AND_USAGE_DATA,
      },
    ],
    onError: (error: ApolloError) => {
      // @ts-expect-error
      const message = error.graphQLErrors[0].details[0].message ?? error.message;
      handleError(message);
    },
    onCompleted: () => {
      onClose();
    },
  });
  const [updateFilter] = useMutation(UPDATE_FILTER, {
    refetchQueries: [
      {
        query: GET_BILLING_AND_USAGE_DATA,
      },
    ],
    onError: (error: ApolloError) => {
      // @ts-expect-error
      const message = error.graphQLErrors[0].details[0].message ?? error.message;
      handleError(message);
    },
    onCompleted: () => {
      onClose();
    },
  });

  const validationSchema = yup.object({
    filterValue: yup.string().required('Please enter a filter.'),
  });

  const isEdit = !!filter.id;

  const handleSubmit = (values: FormValues): void => {
    if (isEdit) {
      updateFilter({
        variables: {
          categoryId: filter.categoryId,
          filterId: filter.id,
          filterRequest: { filterValue: values.filterValue, disabledUntil: filter.disabledUntil },
        },
      });
    } else {
      createFilter({
        variables: {
          categoryId: filter.categoryId,
          filterRequest: { filterValue: values.filterValue, disabledUntil: null },
        },
      });
    }
  };

  const title = isEdit ? 'Edit Discard Filter' : 'Add Discard Filter';
  const filterName = `${filter.filterName} Category`;
  return (
    <Formik validationSchema={validationSchema} onSubmit={handleSubmit} initialValues={filter}>
      {({ handleChange, values, touched, errors, submitForm }): JSX.Element => (
        <>
          <div className="SectionHeader CostManagementTableForm__Title">{title}</div>
          <p className="Filter FilterForm__Title">{filterName}</p>
          <ResizableTextArea
            name="filterValue"
            onChange={handleChange}
            value={values.filterValue}
            label="Logs to Discard"
            minRows={5}
            inputClassName="Filter FilterForm__TextArea"
            placeholder={
              "Enter a filter that matches the log sources and files to discard from this category \nE.g.: serverHost contains 'frontend'"
            }
            isInvalid={touched.filterValue && !!errors.filterValue}
            errorMessage={errors.filterValue}
            testId={ELEMENT.planPageDiscardFilterFormFilterInput}
          />
          <div className="Filter FilterForm__Buttons">
            <Button
              variant={ButtonVariant.PRIMARY}
              onClick={submitForm}
              testId={ELEMENT.planPageDiscardFilterFormSaveButton}
            >
              Save
            </Button>
            <Button
              variant={ButtonVariant.SECONDARY}
              onClick={onClose}
              testId={ELEMENT.planPageDiscardFilterFormCancelButton}
            >
              Cancel
            </Button>
          </div>
        </>
      )}
    </Formik>
  );
};
